import { useEffect } from 'react';
import { api, refreshSession, setLogoutInProgress } from '@/lib/api/client';
import { isApiError } from '@/lib/api/errors';
import { capabilitiesFor } from '@/lib/auth/capabilities';
import { installGlobalErrorReporting } from '@/lib/log/clientLog';
import { useAuthStore } from '@/store/useAuthStore';
import { useProfileStore, type Profile } from '@/store/useProfileStore';

/**
 * Sign-in and session lifecycle against sreg-api's cookie sessions.
 *
 * SREG users are onboarded operationally (roles set by DevOps), so there is
 * no /exists or /register here — the app never creates accounts.
 */

interface WhoAmI {
  id: string;
  email?: string;
  profiles: Profile[];
  /** sreg-api device MFA: this session is MFA-pending until the device is verified. */
  mfaRequired?: boolean;
}

/** Pick the profile the session acts as: the default one, else the first. */
function pickProfile(profiles: Profile[]): Profile | undefined {
  return profiles.find((p) => p.default) ?? profiles[0];
}

export async function whoAmI(): Promise<void> {
  const me = await api.get<WhoAmI>('/who-am-i');
  const profile = pickProfile(me.profiles ?? []);
  const auth = useAuthStore.getState();
  if (!profile) {
    useProfileStore.getState().setMe(null);
    auth.setSessionStatus('no-access');
    return;
  }
  useProfileStore.getState().setMe({ userId: me.id, profile });
  if (me.mfaRequired) {
    auth.setSessionStatus('mfa');
    return;
  }
  const ready = capabilitiesFor(profile.roles).size > 0;
  auth.setSessionStatus(ready ? 'ready' : 'no-access');
  if (ready) registerDevice();
}

/** Email a one-time sign-in code. sreg-api answers the same whether or not the account exists. */
export async function requestSignInCode(email: string): Promise<void> {
  await api.put('/access-code/email', undefined, { email });
}

/** Sign in with an emailed code; this also verifies the device for two weeks. */
export async function signInWithCode(email: string, code: string, stayLoggedIn: boolean): Promise<void> {
  await api.post('/login-code', { email, password: code, stayLoggedIn });
  useAuthStore.getState().setAuthenticated(true);
  useAuthStore.getState().setSessionStatus('restoring');
  await whoAmI();
}

/** Browser details for this device (the device id itself is an HttpOnly cookie). Best effort. */
function registerDevice(): void {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const os = /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : undefined;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  void api
    .put('/device', { name: os ? `${browser} on ${os}` : browser, platform: 'web', operatingSystem: os, model: ua.slice(0, 250) })
    .catch(() => undefined);
}

export async function signIn(email: string, password: string, stayLoggedIn: boolean): Promise<void> {
  await api.post('/login', { email, password, stayLoggedIn });
  useAuthStore.getState().setAuthenticated(true);
  useAuthStore.getState().setSessionStatus('restoring');
  await whoAmI();
}

/** Confirms the cookie session at boot and loads the profile. */
export async function restoreSession(): Promise<void> {
  const auth = useAuthStore.getState();
  if (!auth.isAuthenticated) {
    auth.setSessionStatus('idle');
    return;
  }
  auth.setSessionStatus('restoring');
  try {
    await whoAmI();
  } catch (err) {
    // A 401 that survived the client's refresh: no usable session. Signed out, not an outage.
    if (isApiError(err) && err.status === 401) {
      signOutLocally();
      return;
    }
    if (useAuthStore.getState().isAuthenticated) auth.setSessionStatus('error');
  }
}

/** Clear local state only (the server session is already gone or unusable). */
export function signOutLocally(): void {
  useProfileStore.getState().setMe(null);
  useAuthStore.getState().setAuthenticated(false);
  useAuthStore.getState().setSessionStatus('idle');
}

export async function signOut(): Promise<void> {
  setLogoutInProgress(true);
  try {
    await api.post('/auth/logout');
  } catch {
    /* clearing locally regardless */
  } finally {
    signOutLocally();
    setLogoutInProgress(false);
  }
}

// The access token lives 30 minutes; refresh well before that while the app is
// open so long chats (and the BFF, which can't refresh) never see an expired cookie.
const KEEPALIVE_MS = 20 * 60 * 1000;

export function InitAuth() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  useEffect(() => {
    installGlobalErrorReporting();
    void restoreSession();
  }, []);
  useEffect(() => {
    if (!isAuthenticated) return;
    const t = window.setInterval(() => void refreshSession(), KEEPALIVE_MS);
    return () => window.clearInterval(t);
  }, [isAuthenticated]);
  return null;
}
